import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { IonButton, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { notificationsOutline } from 'ionicons/icons';

import { MobilePushService } from '../../core/native/mobile-push.service';

/**
 * MOB-100 Stage 26 — first-launch push permission prompt.
 *
 * Per BRD §4.11.1: render the Hub first, then prompt. Prompting BEFORE
 * the Hub renders has dramatically lower acceptance rates. The Stage 26
 * gate is wired through `MobilePushService.shouldPrompt()` which is
 * `true` only when:
 *
 *   1. The user is authenticated (`TokenBag` non-empty).
 *   2. `markHubRendered()` has been called (the Hub placeholder /
 *      Stage 19 Hub does that on first paint).
 *   3. The first-launch prompt has not yet been shown.
 *   4. The system permission state is still `prompt`.
 *
 * The dialog is friendly, not coercive: the user can dismiss without
 * granting and the app continues to function (push permission denied
 * is NOT a hard blocker per the prompt). After dismissal we never
 * re-open this dialog — Settings > Notifications surfaces an "Open
 * system settings" link for re-enabling later.
 *
 * Mounted in the root `<App>` template so it can fly in over any tab.
 */
@Component({
  selector: 'mob-push-permission-prompt',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonIcon],
  template: `
    @if (visible()) {
      <div class="mob-push-prompt__backdrop" (click)="dismiss()" aria-hidden="true"></div>
      <div
        class="mob-push-prompt__container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mob-push-prompt-title"
      >
        <div class="mob-push-prompt__content">
          <div class="mob-push-prompt__icon" aria-hidden="true">
            <ion-icon name="notifications-outline" />
          </div>
          <h2 id="mob-push-prompt-title" class="mob-push-prompt__title">
            Stay in the loop during the event
          </h2>
          <p class="mob-push-prompt__copy">
            Get session reminders, direct messages, and live event updates the moment
            they happen. You can change this any time in Settings.
          </p>
          <div class="mob-push-prompt__actions">
            <ion-button
              expand="block"
              shape="round"
              (click)="accept()"
              [disabled]="busy()"
            >
              {{ busy() ? 'Just a moment…' : 'Turn on notifications' }}
            </ion-button>
            <ion-button
              expand="block"
              shape="round"
              fill="clear"
              (click)="dismiss()"
              [disabled]="busy()"
            >
              Not now
            </ion-button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .mob-push-prompt__backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        backdrop-filter: blur(3px);
        z-index: 1000;
      }
      .mob-push-prompt__container {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        z-index: 1001;
        padding: env(safe-area-inset-bottom, 0) 0;
      }
      .mob-push-prompt__content {
        background: var(--vevents-color-bg, #ffffff);
        color: var(--vevents-color-text, #0f172a);
        width: 100%;
        max-width: 32rem;
        border-radius: 20px 20px 0 0;
        padding: 1.75rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom, 0));
        text-align: center;
        box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.18);
        animation: mob-push-slide 0.24s ease-out;
      }
      .mob-push-prompt__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: rgba(var(--ion-color-primary-rgb, 56, 189, 248), 0.15);
        color: var(--vevents-color-primary, #38bdf8);
        font-size: 1.6rem;
        margin-bottom: 0.5rem;
      }
      .mob-push-prompt__title {
        margin: 0 0 0.4rem;
        font-size: 1.15rem;
      }
      .mob-push-prompt__copy {
        margin: 0 0 1.1rem;
        opacity: 0.78;
        font-size: 0.95rem;
        line-height: 1.4;
      }
      .mob-push-prompt__actions {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }
      @keyframes mob-push-slide {
        from {
          transform: translateY(40%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `
  ]
})
export class PushPermissionPromptComponent {
  private readonly push = inject(MobilePushService);

  protected readonly busy = signal(false);

  /**
   * `visible` is computed as `shouldPrompt() && !busy() === false`.
   * We render whenever the service says we should AND we're not in a
   * post-dismiss transient state.
   */
  protected readonly visible = this.push.shouldPrompt;

  async accept(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    try {
      await this.push.requestPermissionAndRegister();
    } catch (err) {
      console.warn('[PushPermissionPrompt] accept() failed:', err);
    } finally {
      this.busy.set(false);
    }
  }

  async dismiss(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    try {
      // Mark the prompt as shown so we never re-open it this install.
      // The user can re-enable from Settings > Notifications later.
      await this.push.markPromptShown();
    } finally {
      this.busy.set(false);
    }
  }

  constructor() {
    addIcons({
      'notifications-outline': notificationsOutline
    });
  }
}
